import React from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import {
  UserProfile,
  LAB_INTEGRATION_VIRTUAL_CLINIC,
} from '../../models/UserProfile';
import { copyToClipboard } from '../../utils/clipboard';
import { visibleExceptProd } from '../../utils/featureVisibility';

interface ProfilePageProps {
  userProfile: UserProfile;
  onSaveProfile: (details: ProfileDetails) => void;
  // Saves on toggle rather than on a Save button, because this one is consent
  // and an unsaved revocation is a lie. See the container's handler.
  onToggleLabIntegration: (integration: string, enabled: boolean) => void;
}

export interface ProfileDetails {
  firstName: string;
  lastName: string;
  practitionerType: string;
  referralSource: string;
}

const NAME_MAX_LENGTH = 40;
// The limits of the Cognito custom attributes these answers were first
// collected into at Create Account (custom:practitioner_type,
// custom:referral_source).
const PRACTITIONER_TYPE_MAX_LENGTH = 256;
const REFERRAL_SOURCE_MAX_LENGTH = 128;

/**
 * "Profile" — who the practitioner is.
 *
 * Nothing here reaches a report. Prepared By and the Custom Logo passed through
 * this page on their way out of Settings, but both are printed on the reports
 * the practitioner produces, so they went on to Report Settings with the rest
 * of the report's appearance. What is left is what is genuinely account-level:
 * the name shown in the Account menu, the two questions asked at Create
 * Account, and the sign-in email.
 */
export const ProfilePage: React.FC<ProfilePageProps> = ({
  userProfile,
  onSaveProfile,
  onToggleLabIntegration,
}) => {
  // All four stay LOCAL until Save, rather than pushing each keystroke into the
  // shared profile the way Report Settings' own fields do: the Account menu
  // reads that profile, so a half-typed name would appear up there and then
  // vanish on the next reload.
  const [firstName, setFirstName] = React.useState(userProfile.firstName || '');
  const [lastName, setLastName] = React.useState(userProfile.lastName || '');
  const [practitionerType, setPractitionerType] = React.useState(
    userProfile.practitionerType || '',
  );
  const [referralSource, setReferralSource] = React.useState(
    userProfile.referralSource || '',
  );

  const boundedSetter = (
    setter: (value: string) => void,
    maxLength: number,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.length > maxLength) {
      return;
    }
    setter(val);
  };

  const handleFirstNameChange = boundedSetter(setFirstName, NAME_MAX_LENGTH);
  const handleLastNameChange = boundedSetter(setLastName, NAME_MAX_LENGTH);
  const handlePractitionerTypeChange = boundedSetter(
    setPractitionerType,
    PRACTITIONER_TYPE_MAX_LENGTH,
  );
  const handleReferralSourceChange = boundedSetter(
    setReferralSource,
    REFERRAL_SOURCE_MAX_LENGTH,
  );

  const handleSave = () =>
    onSaveProfile({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      practitionerType: practitionerType.trim(),
      referralSource: referralSource.trim(),
    });

  // The identity sub, which is also the practitionerId stored on every client
  // and test this account owns. It is what the Virtual Clinic integration keys
  // on, so what is copied here has to be the id itself, untrimmed and
  // unformatted.
  const practitionerId = (userProfile.id || '').trim();
  const [idCopied, setIdCopied] = React.useState(false);
  const copyTimer = React.useRef<number | undefined>(undefined);

  // The confirmation is on a timer, so it has to be cancelled if the
  // practitioner navigates away inside the two seconds.
  React.useEffect(
    () => () => {
      window.clearTimeout(copyTimer.current);
    },
    [],
  );

  // Read straight off the profile rather than mirrored into local state: the
  // container saves on toggle and the spinner covers the round trip, so the
  // box moves when the save lands. That is also what makes the failure path
  // honest — the container puts the value back and the box follows it.
  const virtualClinicAllowed = (
    userProfile.enabledLabIntegrations || []
  ).indexOf(LAB_INTEGRATION_VIRTUAL_CLINIC) > -1;

  const handleVirtualClinicToggle = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onToggleLabIntegration(LAB_INTEGRATION_VIRTUAL_CLINIC, e.target.checked);
  };

  const handleCopyPractitionerId = () => {
    if (!practitionerId) {
      return;
    }
    copyToClipboard(practitionerId).then((copied: boolean) => {
      if (!copied) {
        // Saying "Copied!" over an empty clipboard is the worst outcome here:
        // the practitioner pastes nothing into Virtual Clinic and blames the
        // integration. Same reasoning as the Clients table's Copy Client ID.
        alert(
          'Could not copy the Practitioner ID to the clipboard. Please try again, or email support@labsmarts.com.',
        );
        return;
      }
      setIdCopied(true);
      window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => {
        setIdCopied(false);
      }, 2000);
    });
  };

  return (
    <div>
      {/* They were on this page until now, and before that in Settings. A
          pointer costs one line and saves a hunt through three screens. */}
      <p className="text-muted">
        Looking for <strong>Prepared By</strong> or your{' '}
        <strong>Custom Logo</strong>? They are in{' '}
        <Link to="/settings">Report Settings</Link>, under the Test Results
        menu, with the rest of what appears on your reports.
      </p>

      <h5>Practitioner Details</h5>
      <p className="text-muted">
        Your name, as it appears in the Account menu, and the details you gave
        when you created your account.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formFirstName">
          <Form.Label column sm={3}>
            First Name
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '240px' }}
              placeholder="e.g. MaryAnn"
              value={firstName}
              onChange={handleFirstNameChange}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="formLastName">
          <Form.Label column sm={3}>
            Last Name
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '240px' }}
              placeholder="e.g. Marks"
              value={lastName}
              onChange={handleLastNameChange}
            />
            <Form.Text className="text-muted">
              This is the name shown in the Account menu at the top of the
              screen. It does not appear on reports. To put a name on your
              reports, use <strong>Prepared By</strong> in Report Settings.
            </Form.Text>
          </Col>
        </Form.Group>

        {/* Not required here, unlike at Create Account: every account made
            before the questions existed has neither answer, and a name change
            must not be blocked on filling them in. */}
        <Form.Group as={Row} controlId="formPractitionerType">
          <Form.Label column sm={3}>
            Practitioner Type
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '340px', maxWidth: '100%' }}
              placeholder="e.g. Naturopathic Doctor, Nutrition Consultant"
              value={practitionerType}
              onChange={handlePractitionerTypeChange}
            />
            <Form.Text className="text-muted">
              List every credential that applies. This is for LabSmarts'
              information only and does not appear on any report.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="formReferralSource">
          <Form.Label column sm={3}>
            How Did You Hear About Us?
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '340px', maxWidth: '100%' }}
              placeholder="e.g. a colleague, a webinar, a podcast"
              value={referralSource}
              onChange={handleReferralSourceChange}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Col sm={{ span: 9, offset: 3 }}>
            <Button variant="primary" onClick={handleSave}>
              Save Practitioner Details
            </Button>
          </Col>
        </Form.Group>
      </Form>

      <h5 className="mt-4">Sign-in Email</h5>
      <p className="text-muted">
        The address you sign in with, and where account and subscription email
        is sent.
      </p>
      <hr />

      <Form className="mb-5">
        <Form.Group as={Row} controlId="formEmail">
          <Form.Label column sm={3}>
            Email
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              type="text"
              style={{ width: '340px' }}
              value={userProfile.email || ''}
              readOnly
              disabled
            />
            <Form.Text className="text-muted">
              This address cannot be changed here. If you need it updated, email{' '}
              <a href="mailto:support@labsmarts.com">support@labsmarts.com</a>{' '}
              and we will take care of it. Your clients, tests and report
              settings all stay with your account when it changes.
            </Form.Text>
          </Col>
        </Form.Group>
      </Form>

      {/* Dev and slava only while the Virtual Clinic integration is built: the
          field this ID is pasted into does not exist on their side yet, so on
          the live site the block would be an instruction nobody can follow.
          Last on the page when it does ship, because it is a once-per-account
          setup step, not something a practitioner comes back to.

          The gate also keeps the two environments' IDs apart during testing.
          The ID is a Cognito sub and prod is a SEPARATE pool from slava, so the
          same practitioner has a different Practitioner ID on each site. While
          only slava shows it, the only ID anyone can copy is the slava one,
          which is the one the integration is being tested against. */}
      {visibleExceptProd() && (
        <>
          {/* Named for what it DOES, not for the value it hands over. The
              practitioner is here to connect Virtual Clinic; the Practitioner
              ID is the mechanism, and "LabSmarts Practitioner ID" is kept for
              the one place it is a proper noun, the field they fill in on the
              Virtual Clinic side. */}
          <h5 className="mt-4">Virtual Clinic Integration</h5>
          <p className="text-muted">
            Connect Virtual Clinic so Labcorp results ordered there come into
            LabSmarts on their own, with no upload.
          </p>
          <hr />

          <Form className="mb-5">
            {/* The permission comes BEFORE the ID, because that is the order
                the two are done in and the order they matter in: the ID is
                just a string until this box is ticked.

                Copying the ID deliberately does NOT tick it. Handing an
                identifier to another system is not the same act as letting
                that system write into a client's chart, and only one of the
                two is reversible from here. */}
            <Form.Group as={Row}>
              <Form.Label column sm={3}>
                Permission
              </Form.Label>
              <Col sm={9}>
                <Form.Check
                  type="checkbox"
                  id="allowVirtualClinicResults"
                  label="Allow Virtual Clinic to send test results to this account"
                  checked={virtualClinicAllowed}
                  onChange={handleVirtualClinicToggle}
                />
                {/* State-neutral wording: the same sentence has to read
                    correctly with the box ticked and unticked. */}
                <Form.Text className="text-muted">
                  While this is checked, results Virtual Clinic sends arrive as
                  new tests on the matching client, ready to analyze.
                  Unchecking this stops them: LabSmarts turns away anything
                  Virtual Clinic sends afterwards. Tests that already arrived
                  stay where they are.
                </Form.Text>
                {/* The other half of the setup, and the reason it is mentioned
                    HERE: ticking this box is what puts that column on the
                    Clients table, so a practitioner who is not told goes
                    looking for it before it exists. The column is gated on
                    this same consent (client-display.tsx). */}
                <Form.Text className="text-muted">
                  Checking this also adds a <strong>Copy Client ID</strong>{' '}
                  column to the clients table under{' '}
                  <strong>Manage Clients</strong>. Each client needs their own
                  ID pasted into the <strong>LabSmarts Client ID</strong> field
                  on that client's Virtual Clinic profile. That is what matches
                  an incoming result to the right person, so a client with no
                  ID in Virtual Clinic gets no results.
                </Form.Text>
              </Col>
            </Form.Group>

            {/* No controlId: the row has no input for a label to point at,
                and a dangling htmlFor is worse than none. */}
            <Form.Group as={Row}>
              <Form.Label column sm={3}>
                Practitioner ID
              </Form.Label>
              <Col sm={9}>
                {/* The button IS the field. Nothing reads a 36-character
                    identifier, they only move it, so the page shows the action
                    and keeps the id in the tooltip for the one time it is
                    wanted: checking it against what is already sitting in the
                    Virtual Clinic field. Same call as the Clients table's Copy
                    Client ID button, which hovers to reveal for the same
                    reason. */}
                <Button
                  variant="outline-primary"
                  style={{ whiteSpace: 'nowrap' }}
                  title={
                    !practitionerId
                      ? 'No Practitioner ID on this account yet'
                      : idCopied
                      ? 'Copied!'
                      : `Copy Practitioner ID: ${practitionerId}`
                  }
                  disabled={!practitionerId}
                  onClick={handleCopyPractitionerId}
                >
                  <FontAwesomeIcon icon={idCopied ? 'check' : 'copy'} />
                  {idCopied ? ' Copied!' : ' Copy Practitioner ID'}
                </Button>
                <Form.Text className="text-muted">
                  Copy this ID and paste it into the{' '}
                  <strong>LabSmarts Practitioner ID</strong> field on your
                  Virtual Clinic practitioner account. That is a one time step,
                  and once it is saved there the results start arriving by
                  themselves.
                </Form.Text>
                <Form.Text className="text-muted">
                  The ID goes straight to the clipboard, so paste it rather than
                  typing it. To check it against what you already pasted into
                  Virtual Clinic, hover over the button and the full ID appears.
                  Give this ID only to software you are connecting to LabSmarts.
                </Form.Text>
              </Col>
            </Form.Group>
          </Form>
        </>
      )}
    </div>
  );
};
